import { PERAHAN_INFO } from "@/lib/constants";

export default function About() {
  const timeline = [
    {
      year: "1983",
      title: "Foundation",
      description: "Perahan founded by Nasra Omar in Karachi with a vision to revive heritage clothing"
    },
    {
      year: "1990s",
      title: "Artisan Partnerships",
      description: "Established strong relationships with local block printing artisans and craftspeople"
    },
    {
      year: "2000s",
      title: "Heritage Preservation",
      description: "Documented traditional techniques and trained new generation of craftspeople"
    },
    {
      year: "2010s",
      title: "Modern Integration",
      description: "Blended traditional craftsmanship with contemporary design sensibilities"
    },
    {
      year: "2020s",
      title: "Digital Presence",
      description: "Expanded reach while maintaining commitment to handcrafted quality"
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="py-20 bg-[var(--perahan-ivory)] px-4">
        <div className="max-w-6xl mx-auto text-center">
          <h1 className="text-5xl font-serif font-bold text-[var(--perahan-charcoal)] mb-6">
            Our Story
          </h1>
          <p className="text-xl text-[var(--perahan-gray)] max-w-3xl mx-auto leading-relaxed">
            Four decades of preserving tradition, celebrating craftsmanship, and creating timeless fashion
          </p>
        </div>
      </section>

      {/* Founder Story */}
      <section className="py-20 bg-white px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-serif font-bold text-[var(--perahan-charcoal)] mb-6">
                The Vision of {PERAHAN_INFO.founder}
              </h2>
              <p className="text-lg mb-6 leading-relaxed">
                Founded by <strong>{PERAHAN_INFO.founder}</strong> in {PERAHAN_INFO.founded}, Perahan began as a passion for 
                block-printed fabric and traditional fashion. A graduate of NCA (National College of Arts), 
                Nasra's vision was to revive heritage clothing with a timeless appeal that would resonate with modern sensibilities.
              </p>
              <p className="text-lg mb-6 leading-relaxed">
                For over 40 years, Perahan has offered handcrafted fashion rooted in South Asian identity — 
                with every print telling a story of patience, skill, and love. What started as one woman's dream 
                has grown into a beacon of cultural preservation and artistic excellence.
              </p>
              
              <div className="border-l-4 border-[var(--perahan-maroon)] pl-6 mb-8">
                <p className="text-lg italic text-[var(--perahan-gray)] mb-2">
                  "We believe in slow fashion, cultural sustainability, and keeping ancient traditions alive through contemporary design. 
                  Every piece we create is not just clothing—it's a connection to our heritage."
                </p>
                <p className="text-sm font-medium text-[var(--perahan-maroon)]">
                  — {PERAHAN_INFO.founder}, Founder
                </p>
              </div>
            </div>
            <div>
              <img 
                src="https://pixabay.com/get/gf5bb8a573481985b19fa965e0ecf34efe301f875baa4358bc2d9847066636e1f141f34a1062d87b19827e27a263567bbbcd76c2ede162f510e1714c86ad5d9c7_1280.jpg"
                alt={`Portrait of ${PERAHAN_INFO.founder}, founder of Perahan, surrounded by traditional fabrics`}
                className="rounded-lg shadow-lg w-full h-auto"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-20 bg-[var(--perahan-ivory)] px-4">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-serif font-bold text-[var(--perahan-charcoal)] mb-4">
              Our Journey Through Time
            </h2>
            <p className="text-lg text-[var(--perahan-gray)]">
              Four decades of growth, preservation, and innovation
            </p>
          </div>

          <div className="space-y-8">
            {timeline.map((milestone, index) => (
              <div key={index} className="flex items-start space-x-6">
                <div className="flex-shrink-0">
                  <div className="w-16 h-16 bg-[var(--perahan-maroon)] rounded-full flex items-center justify-center">
                    <span className="text-white font-bold text-sm">{milestone.year}</span>
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-serif font-semibold text-[var(--perahan-charcoal)] mb-2">
                    {milestone.title}
                  </h3>
                  <p className="text-[var(--perahan-gray)] leading-relaxed">
                    {milestone.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Philosophy */}
      <section className="py-20 bg-white px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-serif font-bold text-[var(--perahan-charcoal)] mb-4">
              Our Philosophy
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center p-6">
              <div className="w-16 h-16 bg-[var(--perahan-maroon)] rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-white text-2xl">🎨</span>
              </div>
              <h3 className="text-xl font-serif font-semibold text-[var(--perahan-charcoal)] mb-3">
                Artistic Excellence
              </h3>
              <p className="text-[var(--perahan-gray)]">
                Every design is thoughtfully created, honoring traditional motifs while embracing contemporary aesthetics.
              </p>
            </div>

            <div className="text-center p-6">
              <div className="w-16 h-16 bg-[var(--perahan-terracotta)] rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-white text-2xl">🌱</span>
              </div>
              <h3 className="text-xl font-serif font-semibold text-[var(--perahan-charcoal)] mb-3">
                Sustainable Practice
              </h3>
              <p className="text-[var(--perahan-gray)]">
                We use natural dyes, organic fabrics, and traditional techniques that have minimal environmental impact.
              </p>
            </div>

            <div className="text-center p-6">
              <div className="w-16 h-16 bg-[var(--perahan-gold)] rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-white text-2xl">🤝</span>
              </div>
              <h3 className="text-xl font-serif font-semibold text-[var(--perahan-charcoal)] mb-3">
                Community Support
              </h3>
              <p className="text-[var(--perahan-gray)]">
                We provide fair wages and preserve traditional skills by working directly with local artisans.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-20 bg-[var(--perahan-ivory)] px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-serif font-bold text-[var(--perahan-charcoal)] mb-6">
            Be Part of Our Story
          </h2>
          <p className="text-lg text-[var(--perahan-gray)] mb-8 max-w-2xl mx-auto">
            When you choose Perahan, you're not just buying clothing—you're supporting a legacy of craftsmanship 
            and helping preserve cultural traditions for future generations.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={() => {
                window.location.href = '/collection';
              }}
              className="btn-primary"
            >
              Explore Our Collection
            </button>
            <button 
              onClick={() => {
                window.location.href = '/contact';
              }}
              className="btn-terracotta"
            >
              Visit Our Store
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
